import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { NameEquivalencesView, TryNameView } from '@nest-recruit/shared';
import { ApiError, apiGet, apiPost, apiPut } from '../../../lib/api-client';

// §L2's name table at ADMIN (S31 Q23; Admin redesign batch 2): the table with its version and "Names" counts, a save
// that carries the version it was edited from, and "Try a name".

export const NAME_MATCHING_KEY = ['admin', 'name-matching'] as const;
const TRY_NAME_MIN = 2;

export function useNameTable(isEnabled: boolean): UseQueryResult<NameEquivalencesView, ApiError> {
  return useQuery<NameEquivalencesView, ApiError>({
    queryKey: NAME_MATCHING_KEY,
    queryFn: () => apiGet<NameEquivalencesView>('/admin/name-matching'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function useSaveNameTable(): UseMutationResult<NameEquivalencesView, ApiError, { readonly groups: readonly (readonly string[])[]; readonly basedOn: string | undefined }> {
  const queryClient = useQueryClient();
  return useMutation<NameEquivalencesView, ApiError, { readonly groups: readonly (readonly string[])[]; readonly basedOn: string | undefined }>({
    mutationFn: ({ groups, basedOn }) => apiPut<NameEquivalencesView>('/admin/name-matching', { groups, ...(basedOn !== undefined ? { basedOn } : {}) }),
    onSuccess: async (view) => {
      queryClient.setQueryData(NAME_MATCHING_KEY, view);
      // "Try a name" read the old table.
      await queryClient.invalidateQueries({ queryKey: [...NAME_MATCHING_KEY, 'try'] });
    },
    onError: async (error) => {
      // Someone else saved: read theirs, so the page can say so and offer it.
      if (error.status === 409) await queryClient.invalidateQueries({ queryKey: NAME_MATCHING_KEY });
    },
  });
}

/** "Try a name", read with the saved table. Waits for two letters; the caller debounces the typing. */
export function useTryName(name: string, isEnabled: boolean): UseQueryResult<TryNameView, ApiError> {
  const trimmed = name.trim();
  return useQuery<TryNameView, ApiError>({
    queryKey: [...NAME_MATCHING_KEY, 'try', trimmed],
    queryFn: () => apiPost<TryNameView>('/admin/name-matching/try', { name: trimmed }),
    enabled: isEnabled && trimmed.length >= TRY_NAME_MIN,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}
